import { Link, useSearchParams } from 'react-router';
import { RefreshCcw, Save } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { sessionScopeKey, useSessionScopeFence } from '@/hooks/useSessionScope';
import { changedSettingsFields, useSettingsDraft } from './useSettingsDraft';
import { SettingsDraftActions } from './SettingsDraftActions';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import { Badge, Button, EmptyState, ErrorState, Spinner } from '@helios/ui';
import { useAuth } from '@/features/auth/authContext';
import { useAdminRoles } from '@/hooks/useAdmin';
import { useSettingsBootstrap, useUpdateOrganizationSettings } from '@/hooks/useSettings';
import {
  SettingsLoadingState,
  SettingsMutationBanner,
  toOrganizationForm,
  type OrganizationForm,
} from '@/features/settings/settingsShared';
import {
  SettingsField,
  SettingsPageFrame,
  SettingsSection,
  SettingsTable,
  SettingsTableRow,
  SettingsTd,
  SettingsToggle,
  settingsControlClass,
} from '@/features/settings/settingsChrome';
import type { NotificationChannel } from '@/services/settingsClient';
import { SettingsUserDirectory } from './SettingsUserDirectory';
import { Pagination } from './SettingsAdminControls';

export function AccessSettingsPage() {
  const scope = useSessionScopeFence();
  return <AccessWorkspace key={JSON.stringify(sessionScopeKey(scope.identity))} />;
}

function AccessWorkspace() {
  const auth = useAuth();
  const { permissions } = auth;
  const [searchParams] = useSearchParams();
  const platform = hasPermission(permissions, PERMISSIONS.PLATFORM_ADMIN);
  const currentTenantId = auth.tenant?.id ?? auth.user?.tenantId;
  const directoryTenantId =
    platform && searchParams.get('directoryScope') === 'all'
      ? undefined
      : platform
        ? searchParams.get('tenantId') || currentTenantId
        : currentTenantId;
  const [rolePage, setRolePage] = useState({ tenantId: directoryTenantId, page: 1 });
  const rolesPage = rolePage.tenantId === directoryTenantId ? rolePage.page : 1;
  const canEditPolicy = hasPermission(permissions, PERMISSIONS.SETTINGS_MANAGE);
  const canReadUsers = hasPermission(permissions, PERMISSIONS.USERS_MANAGE);
  const canReadRoles = hasPermission(permissions, PERMISSIONS.ROLES_MANAGE);

  const bootstrapQuery = useSettingsBootstrap();
  const updateOrganization = useUpdateOrganizationSettings();
  const rolesQuery = useAdminRoles(
    { page: rolesPage, pageSize: 25, tenantId: directoryTenantId },
    canReadRoles,
  );
  const [notice, setNotice] = useState<{ scope: string; message: string } | null>(null);
  const bootstrap = bootstrapQuery.data;

  const { identity, isCurrent } = useSessionScopeFence(PERMISSIONS.SETTINGS_MANAGE);
  const draft = useSettingsDraft(
    bootstrap?.organization
      ? {
          form: toOrganizationForm(bootstrap.organization),
          version: bootstrap.organization.version,
        }
      : null,
  );
  const form = draft.form;
  const setForm = draft.change;

  if (bootstrapQuery.isLoading && canEditPolicy) {
    return <SettingsLoadingState label="Loading access control…" />;
  }

  if (canEditPolicy && (!bootstrap || bootstrapQuery.isError)) {
    return (
      <ErrorState
        title="Access control unavailable"
        description="The protected settings endpoint did not return organization access policy for this tenant."
        action={
          <Button
            type="button"
            variant="outline"
            size="xs"
            onClick={() => void bootstrapQuery.refetch()}
          >
            <RefreshCcw className="h-3.5 w-3.5" aria-hidden="true" />
            Retry
          </Button>
        }
      />
    );
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form || !draft.baseline || draft.version === null || !draft.dirty) return;
    const origin = identity;
    try {
      const saved = await updateOrganization.mutateAsync({
        expectedVersion: draft.version,
        ...changedSettingsFields(form, draft.baseline, [
          'requireMfa',
          'allowExternalSharing',
          'defaultNotificationChannel',
        ] satisfies (keyof OrganizationForm)[]),
      });
      if (!isCurrent(origin)) return;
      draft.accept({ form: toOrganizationForm(saved), version: saved.version });
      setNotice({ scope: draft.scope, message: 'Stored access policy saved.' });
    } catch {
      /* Keep edits for explicit conflict resolution. */
    }
  };

  const roles = rolesQuery.data?.items ?? [];

  return (
    <SettingsPageFrame
      eyebrow="Workspace"
      title="Access control"
      description="Roles, permissions, membership, and stored organization policy preferences."
      actions={
        canEditPolicy ? (
          <Button
            type="button"
            variant="outline"
            size="xs"
            onClick={() => void bootstrapQuery.refetch()}
          >
            <RefreshCcw className="h-3.5 w-3.5" aria-hidden="true" />
            Refresh
          </Button>
        ) : null
      }
    >
      <SettingsMutationBanner
        message={notice?.scope === draft.scope ? notice.message : null}
        error={updateOrganization.error}
      />
      <SettingsDraftActions
        dirty={draft.dirty}
        changedOnServer={draft.changedOnServer}
        disabled={updateOrganization.isPending}
        onRevert={() => {
          draft.accept();
          updateOrganization.reset();
          setNotice(null);
        }}
      />

      {canEditPolicy ? (
        <SettingsSection
          id="access-policy"
          title="Tenant policy"
          description={`Policy preferences for your current workspace${auth.tenant?.name ? ` (${auth.tenant.name})` : ''}. These fields do not enable MFA enforcement, sharing restrictions, or email/SMS/webhook delivery.`}
          actions={
            form ? (
              <Button
                type="submit"
                form="settings-access-form"
                size="xs"
                disabled={updateOrganization.isPending || !draft.dirty}
              >
                {updateOrganization.isPending ? (
                  <Spinner className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  <Save className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                Save policy
              </Button>
            ) : null
          }
        >
          {!form ? (
            <EmptyState
              title="No organization"
              description="This tenant has no organization row, so access policy cannot be stored."
            />
          ) : (
            <form
              id="settings-access-form"
              className="grid items-end gap-3 sm:grid-cols-3"
              onSubmit={handleSubmit}
            >
              <SettingsToggle
                label="Require MFA"
                checked={form.requireMfa}
                onChange={(checked) => setForm({ ...form, requireMfa: checked })}
              />
              <SettingsToggle
                label="Allow external sharing"
                checked={form.allowExternalSharing}
                onChange={(checked) => setForm({ ...form, allowExternalSharing: checked })}
              />
              <SettingsField label="Default notification channel">
                <select
                  className={settingsControlClass}
                  value={form.defaultNotificationChannel}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      defaultNotificationChannel: event.target.value as NotificationChannel,
                    })
                  }
                >
                  <option value="IN_APP">In app</option>
                  <option value="EMAIL">Email</option>
                  <option value="SMS">SMS</option>
                  <option value="WEBHOOK">Webhook</option>
                </select>
              </SettingsField>
            </form>
          )}
        </SettingsSection>
      ) : null}

      {canReadUsers ? (
        <SettingsSection
          id="membership"
          title="Membership"
          description="The same live directory as Administration Users, with matching workspace, roles, status, and sign-in access."
          actions={
            <Button size="xs" variant="outline" asChild>
              <Link to={`/settings/users${searchParams.size ? `?${searchParams.toString()}` : ''}`}>
                Open users
              </Link>
            </Button>
          }
        >
          <SettingsUserDirectory />
        </SettingsSection>
      ) : null}

      {canReadRoles ? (
        <SettingsSection
          id="roles"
          title="Roles & permissions"
          description="Permission sets for the directory workspace. User counts match retained accounts in that workspace. Edit grants on Roles."
          actions={
            <Button size="xs" variant="outline" asChild>
              <Link to="/settings/roles">Open roles</Link>
            </Button>
          }
        >
          {rolesQuery.isLoading ? (
            <p className="text-muted-foreground text-xs">Loading roles…</p>
          ) : rolesQuery.isError ? (
            <p className="text-critical text-xs">
              {rolesQuery.error instanceof Error
                ? rolesQuery.error.message
                : 'Roles API did not respond.'}
            </p>
          ) : roles.length === 0 ? (
            <EmptyState title="No roles" description="Create a tenant role on the Roles page." />
          ) : (
            <SettingsTable columns={['Role', 'Scope', 'Users', 'Permissions']} minWidth={560}>
              {roles.map((role) => (
                <SettingsTableRow key={role.id}>
                  <SettingsTd>
                    <p className="text-foreground font-medium">{role.name}</p>
                    <p className="text-2xs text-muted-foreground">
                      {role.description ?? 'No description'}
                    </p>
                  </SettingsTd>
                  <SettingsTd>
                    <Badge variant={role.isSystem ? 'warning' : 'outline'}>
                      {role.isSystem ? 'System' : 'Tenant'}
                    </Badge>
                  </SettingsTd>
                  <SettingsTd className="text-muted-foreground">{role.userCount}</SettingsTd>
                  <SettingsTd className="text-muted-foreground">
                    {role.permissionKeys.length}
                  </SettingsTd>
                </SettingsTableRow>
              ))}
            </SettingsTable>
          )}
          <Pagination
            page={rolesPage}
            totalPages={rolesQuery.data?.meta.totalPages ?? 0}
            onChange={(page) => setRolePage({ tenantId: directoryTenantId, page })}
            label="Roles"
          />
        </SettingsSection>
      ) : null}

      <SettingsSection
        id="sessions"
        title="Sessions"
        description="Sign-in session inventory stays on Activity. Idle JWT and silent refresh are not edited here."
        actions={
          canReadUsers ? (
            <Button size="xs" variant="outline" asChild>
              <Link to="/settings/activity">Open activity</Link>
            </Button>
          ) : null
        }
      >
        <p className="text-muted-foreground text-xs">
          Session and token security is reviewed on Activity. This page does not change refresh
          behavior.
        </p>
      </SettingsSection>
    </SettingsPageFrame>
  );
}
